import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shuffle, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const WORDS_BY_GRADE = {
  6: [
    { word: 'BEAUTIFUL', hint: 'Pleasing to look at' }, { word: 'KNOWLEDGE', hint: 'What you learn' },
    { word: 'COURAGE', hint: 'Being brave' }, { word: 'SENTENCE', hint: 'A group of words that makes sense' },
    { word: 'ADVENTURE', hint: 'An exciting experience' }, { word: 'EDUCATION', hint: 'Process of learning' },
    { word: 'IMPORTANT', hint: 'Has great value' }, { word: 'DISCOVER', hint: 'To find something new' },
    { word: 'TREASURE', hint: 'Something very valuable' }, { word: 'CHILDREN', hint: 'Young people' },
  ],
  7: [
    { word: 'ENVIRONMENT', hint: 'The natural world around us' }, { word: 'ACCOMPLISH', hint: 'To achieve or complete' },
    { word: 'CONSEQUENCE', hint: 'Result of an action' }, { word: 'MYSTERIOUS', hint: 'Strange and unknown' },
    { word: 'EXAGGERATE', hint: 'To overstate something' }, { word: 'LITERATURE', hint: 'Written artistic works' },
    { word: 'TECHNOLOGY', hint: 'Use of scientific knowledge' }, { word: 'GOVERNMENT', hint: 'Ruling body of a nation' },
    { word: 'INCREDIBLE', hint: 'Hard to believe, amazing' }, { word: 'PHILOSOPHY', hint: 'Study of fundamental truths' },
  ],
  8: [
    { word: 'PHENOMENON', hint: 'A remarkable occurrence' }, { word: 'CONSCIENTIOUS', hint: 'Very careful and thorough' },
    { word: 'CATASTROPHE', hint: 'A terrible disaster' }, { word: 'ACKNOWLEDGE', hint: 'To accept or admit' },
    { word: 'SOPHISTICATED', hint: 'Complex and refined' }, { word: 'CONTEMPORARY', hint: 'Belonging to the present time' },
    { word: 'CIRCUMSTANCE', hint: 'A condition or situation' }, { word: 'PREDOMINANT', hint: 'Most common or powerful' },
    { word: 'INFRASTRUCTURE', hint: 'Basic systems of a country' }, { word: 'REVOLUTIONARY', hint: 'Causing dramatic change' },
  ]
};

const WordScramble = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const grade = user?.grade_level || 7;
  const WORDS = WORDS_BY_GRADE[grade] || WORDS_BY_GRADE[7];
  const [index, setIndex] = useState(0);
  const [scrambled, setScrambled] = useState('');
  const [userInput, setUserInput] = useState('');
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);
  const total = 8;

  const scrambleWord = useCallback((word) => {
    const arr = word.split('');
    for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
    const result = arr.join('');
    return result === word ? scrambleWord(word) : result;
  }, []);

  useEffect(() => { setScrambled(scrambleWord(WORDS[index].word)); }, [index, scrambleWord]);

  const checkAnswer = () => {
    if (userInput.toUpperCase().trim() === WORDS[index].word) {
      setScore(s => s + 15); setFeedback({ correct: true, msg: 'Unscrambled! Brilliant!' });
    } else {
      setFeedback({ correct: false, msg: `The word was: ${WORDS[index].word}` });
    }
    setTimeout(() => { if (index < total - 1) { setIndex(i => i+1); setUserInput(''); setFeedback(null); } else setCompleted(true); }, 1200);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4">🔤</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Word Wizard!</h2>
          <p className="text-6xl font-black text-purple-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">out of {total * 15} points</p>
          <div className="space-y-3">
            <button onClick={() => { setIndex(0); setScore(0); setCompleted(false); setUserInput(''); setFeedback(null); }} className="w-full py-4 bg-purple-500 text-white rounded-2xl font-bold border-b-4 border-purple-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50" data-testid="word-scramble-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3"><span className="bg-purple-500 text-white px-4 py-2 rounded-2xl font-bold">{index+1}/{total}</span><span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span></div>
        </div>
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl text-center">
          <Shuffle className="w-12 h-12 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-[#1E293B] mb-2">Unscramble the Word!</h2>
          <p className="text-[#64748B] mb-6">Hint: {WORDS[index].hint}</p>
          <div className="flex gap-2 justify-center mb-8 flex-wrap">
            {scrambled.split('').map((ch, i) => (
              <span key={i} className="w-12 h-14 bg-purple-100 border-2 border-purple-300 rounded-xl flex items-center justify-center text-2xl font-black text-purple-700">{ch}</span>
            ))}
          </div>
          <input type="text" value={userInput} onChange={(e) => setUserInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && userInput && checkAnswer()}
            className="w-full text-center text-2xl font-bold p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:border-purple-500 outline-none mb-4 uppercase tracking-widest" placeholder="Type the word..." autoFocus data-testid="scramble-input" />
          {feedback && <div className={`p-4 rounded-2xl font-bold mb-4 ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{feedback.correct ? <CheckCircle className="w-5 h-5 inline mr-2" /> : <XCircle className="w-5 h-5 inline mr-2" />}{feedback.msg}</div>}
          <button onClick={checkAnswer} disabled={!userInput} className="px-10 py-4 bg-purple-500 text-white rounded-2xl font-bold border-b-4 border-purple-700 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50" data-testid="check-scramble-btn">Check</button>
        </div>
      </div>
    </div>
  );
};

export default WordScramble;
